import React, { useCallback, useContext, useState } from "react";
import { useTranslations } from "../i18n/useTranslations";
import { NavContext } from "../Nav/NavContext";
import { closeSearch as closeSearchAction, openSearch as openSearchAction } from "../Nav/reducer";
import { getAPIEndPoint, isBrowser, prefixedWithLang } from "../utils";
import { popularItemsWithLang } from "./data";
import { useDebounce } from "../hooks/useDebounce/useDebounce";

type ChangeEvent = React.ChangeEvent<HTMLInputElement>;

const SUGGESTIONS_QUERY = 
`query SuggestRecipesQuery($input: SuggestRecipesQueryInput) {
  suggestRecipesQuery(input: $input) {
    suggestions
  }
}`

export function useSearch() {
  const [{ isSearchOpen }, dispatch] = useContext(NavContext);
  const { lang } = useTranslations();
  const [query, setQuery] = useState("");
  const [isInputFocused, setInputFocus] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const popularItems = popularItemsWithLang(lang);

  const getSuggestions = useCallback(async(query: string) => {
    const endPoint = getAPIEndPoint(lang)
    const response = await fetch (endPoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: SUGGESTIONS_QUERY,
        variables: { input: {partialQuery: query, count: 5} },
      })
    })
    const { data } = await response.json()
    setSuggestions(data.suggestRecipesQuery?.suggestions || [])
  }, [lang])

  const hasQuery = query.trim() !== "";

  const debounceFilterSuggestionsCb = useDebounce((query: string) => {
    getSuggestions(query)
  }, 200);

  const debounceBlurCb = useDebounce(() => {
    setInputFocus(false);
  }, 200);

  const debounceFocusCb = useDebounce(() => {
    setInputFocus(true);
  }, 200);

  const onInputChange = (e: ChangeEvent) => {
    const value = e.target.value;
    setQuery(value);
    debounceFilterSuggestionsCb(value);
  };

  const onInputFocus = () => {
    debounceFocusCb();
  };
  const onInputBlur = () => {
    debounceBlurCb();
  };

  const search = () => {
    if (!isBrowser()) return;
    window.location.href = prefixedWithLang(lang, `search?s=${query}`);
  };

  const openSearch = useCallback(() => dispatch(openSearchAction()), [dispatch]);
  const closeSearch = useCallback(() => dispatch(closeSearchAction()), [dispatch]);
  const clear = () => setQuery("");

  const toggleSearch = () => (isSearchOpen ? closeSearch() : openSearch());

  return {
    suggestions,
    onInputChange,
    onInputFocus,
    onInputBlur,
    isSearchOpen,
    query,
    hasQuery,
    closeSearch,
    openSearch,
    toggleSearch,
    clear,
    search,
    popularItems,
    isInputFocused,
  };
}
